import { useParams } from 'react-router-dom';
import { AlertCircle, Download, FileArchive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import LoadingSpinner from '@/components/ui/loading-spinner';
import {
  isZipExportInFlight,
  isZipExportUnavailable,
  useDocumentZipExport,
} from '@/hooks/documents/useDocumentZipExport';
import { formatTimeOfDay, formatTimestamp } from '@/utils/dates';

const describeFailure = (reason: string | null | undefined) =>
  reason === 'not_servable'
    ? 'Some of the documents in this download are no longer available.'
    : 'This download could not be prepared.';

/**
 * OV-1405. Where the "your documents are ready" e-mail lands. Serves a
 * background-built ZIP to the one user who requested it; anyone else, or a
 * link past its expiry, gets a plain explanation instead of the file.
 *
 * Deliberately no auto-download here: arriving from an e-mail, the customer
 * should see what they are about to download before it starts.
 */
const ZipExportPage = () => {
  const { exportId } = useParams<{ exportId: string }>();
  const { zipExport, isLoading, error, download } =
    useDocumentZipExport(exportId);

  if (isLoading) return <LoadingSpinner />;

  const expiresAt = zipExport?.expires_at;
  const downloadUrl = zipExport?.download_url;
  const unavailable =
    Boolean(error) || !zipExport || isZipExportUnavailable(zipExport);

  return (
    <div className="mx-auto max-w-xl p-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileArchive className="h-5 w-5" />
            {zipExport?.filename ?? 'Document download'}
          </CardTitle>
          {expiresAt && zipExport?.status === 'ready' && (
            <CardDescription>
              Available until {formatTimestamp(expiresAt)} at{' '}
              {formatTimeOfDay(expiresAt)}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {unavailable && (
            <p className="flex items-start gap-2 text-destructive">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                {zipExport?.status === 'expired'
                  ? 'This download link has expired. Please request the documents again from OneVerse.'
                  : error
                    ? 'This download is not available.'
                    : `${describeFailure(zipExport?.failure_reason)} Please request the documents again from OneVerse.`}
              </span>
            </p>
          )}

          {isZipExportInFlight(zipExport) && (
            <p className="text-muted-foreground">
              Your download is still being prepared. This page will update when
              it’s ready.
            </p>
          )}

          {zipExport && downloadUrl && (
            <>
              {zipExport.skipped_count > 0 && (
                <p className="text-muted-foreground">
                  {zipExport.skipped_count} selected{' '}
                  {zipExport.skipped_count === 1 ? 'file was' : 'files were'}{' '}
                  not available and{' '}
                  {zipExport.skipped_count === 1 ? 'is' : 'are'} not included.
                </p>
              )}
              <Button
                type="button"
                variant="primary"
                className="gap-2"
                onClick={download}
              >
                <Download className="h-4 w-4" />
                Download
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ZipExportPage;
